import React from 'react'
import PropTypes from 'prop-types'
import { ChevronIcon } from '../core/icons'
import { helpers } from '../../helpers'

/**
* HorizontalScroller component that adds scrollbar and navigation arrows to
* a list of objects that overflow's horizontally.
*/
class HorizontalScroller extends React.Component {
  constructor(props) {
    super(props)

    this.uid = helpers.generateID('scroller')
    this.containerSelector = `#${this.uid} .scroller-content`

    // Default State
    this.state = {
      canScroll: false,
      canScrollLeft: false,
      canScrollRight: false,
    }

    // Bind methods
    this.checkControls = this.checkControls.bind(this)
    this.scroll = this.scroll.bind(this)
  }

  /**
  * Checks state of scroller controllers, also adds event listeners for when
  * the user scrolls in the scroller, or when the window is resized to know if
  * the control state has changed.
  */
  componentDidMount() {
    const container = document.querySelector(this.containerSelector)

    container.addEventListener('scroll', this.checkControls)
    window.addEventListener('resize', this.checkControls)
    this.checkControls()
  }

  /**
  * Remove event listeners to check controls.
  */
  componentWillUnmount() {
    const container = document.querySelector(this.containerSelector)

    container.removeEventListener('scroll', this.checkControls)
    window.removeEventListener('resize', this.checkControls)
  }

  /**
  * Checks scroll position and scroller width to see if the user is able to
  * scroll and if they can scroll either left or right (or both). Updates
  * component's state with results.
  */
  checkControls() {
    const container = document.querySelector(this.containerSelector)

    this.setState({
      canScroll: container.scrollWidth > container.clientWidth,
      canScrollLeft: container.scrollLeft > 0,
      canScrollRight: (container.scrollWidth - container.clientWidth - container.scrollLeft) > 0,
    })
  }

  /**
  * Scrolls the slider in the specified direction.
  * @param direction {number} -1 slides left, 1 slides right
  */
  scroll(direction) {
    const container = document.querySelector(this.containerSelector)

    // Kinematics teaches us that t = 2d / (Vi + Vf) so we'll use this
    // to try to make the time to scroll relatively the same based on the
    // distance that will be scrolled.

    const scrollDistance = container.offsetWidth * direction


    const scrollVelocity = 3


    const scrollTime = Math.abs(scrollDistance / scrollVelocity)

    // if trying to scroll left/right but unable to, animate an out and back
    // to provide a little feedback that the action isn't possible.
    if (direction < 0 && !this.state.canScrollLeft) {
      helpers.animate(container, 'scrollLeft', 30, 300, () => {
        helpers.animate(container, 'scrollLeft', -30, 300)
      })
    } else if (direction > 0 && !this.state.canScrollRight) {
      // For some reason some browsers (at least Chrome) allow negative overflow (< 1px)
      // To account for this, we need to add that value in addition to the amount we want
      // to scroll in order for the animation to work as expected.
      const scrollAmount = Math.abs(container.scrollWidth - container.clientWidth - container.scrollLeft) + 30

      helpers.animate(container, 'scrollLeft', -1 * scrollAmount, 200, () => {
        helpers.animate(container, 'scrollLeft', scrollAmount, 200)
      })
    // Otherwise scroll to the next content.
    } else {
      helpers.animate(container, 'scrollLeft', scrollDistance, scrollTime, this.checkControls)
    }
  }

  render() {
    // Figure out what classes the scrolling buttons should have based on state.
    let leftScrollerClass = 'scroll-left'


    let rightScrollerClass = 'scroll-right'

    if (!this.state.canScroll) {
      leftScrollerClass += ' is-hidden'
      rightScrollerClass += ' is-hidden'
    } else {
      if (!this.state.canScrollLeft) {
        leftScrollerClass += ' is-disabled'
      }

      if (!this.state.canScrollRight) {
        rightScrollerClass += ' is-disabled'
      }
    }

    return (
      <div id={this.uid} className="horizontal-scroller">
        <div className={leftScrollerClass} onClick={() => this.scroll(-1)} >
          <ChevronIcon isLeft />
        </div>
        <div className="scroller-content can-override-scrollbar">
          { this.props.children }
        </div>
        <div className={rightScrollerClass} onClick={() => this.scroll(1)}>
          <ChevronIcon isRight />
        </div>
      </div>
    )
  }
}

HorizontalScroller.propTypes = {
  children: PropTypes.any,
}

export default HorizontalScroller
